import { useRef, useState } from "react";
import ToggleRow from "./ToggleRow";
import Accordion from "./Accordion";
import {
    DEFAULT_IMAGE_FILE_NAMES,
    PLACEHOLDERS,
    getTemplateIdByTitle,
    isAllowedImageFile,
    readImageAsDataUri,
} from "../../../../../../utils/planManager/preEnrollmentCustomUtils.js";
import "./CustomizationForm.scss";

/**
 * Camera icon SVG rendered in the dropzone.
 * @returns {import('react').JSX.Element}
 */
const CameraIcon = () => (
    <svg
        className="site-customization-image-field__camera-icon"
        width="28"
        height="24"
        viewBox="0 0 24 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
    >
        <path
            d="M9 2L7.17 4H4C2.9 4 2 4.9 2 6V16C2 17.1 2.9 18 4 18H20C21.1 18 22 17.1 22 16V6C22 4.9 21.1 4 20 4H16.83L15 2H9ZM12 15C9.79 15 8 13.21 8 11C8 8.79 9.79 7 12 7C14.21 7 16 8.79 16 11C16 13.21 14.21 15 12 15Z"
            fill="currentColor"
        />
    </svg>
);

/**
 * Alert circle icon SVG rendered when upload fails.
 * @returns {import('react').JSX.Element}
 */
const AlertIcon = () => (
    <svg
        className="site-customization-image-field__error-icon"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
    >
        <path
            d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z"
            fill="currentColor"
        />
    </svg>
);

/**
 * File name shown under the picker: retrieved name, else the template default.
 * @param {string | { name?: string } | null | undefined} value
 * @param {string} defaultFileName
 * @returns {string}
 */
const getDisplayedImageFileName = (value, defaultFileName) => {
    if (typeof value === "string" && value) {
        return value.includes("/") ? value.slice(value.lastIndexOf("/") + 1) : value;
    }

    if (value && typeof value === "object" && typeof value.name === "string") {
        return value.name;
    }

    return defaultFileName;
};

/**
 * Drag-and-drop file picker that reads image content into Data URI.
 *
 * @param {object} props
 * @param {string} props.label
 * @param {{ name?: string, data?: string } | string | null} props.value - Selected image object, URL, or null.
 * @param {string} [props.hint]
 * @param {string} props.defaultFileName - Retrieved fallback when no custom file is set.
 * @param {(image: { name: string, data: string } | null) => void} props.onChange
 * @returns {import('react').JSX.Element}
 */
const ImageField = ({ label, value, hint, defaultFileName, onChange }) => {
    const fileInputRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [uploadError, setUploadError] = useState(false);

    const displayedFileName = getDisplayedImageFileName(value, defaultFileName);
    const hasCustomImage = Boolean(value);

    const handleFile = async (file) => {
        if (!file) {
            return;
        }

        if (!isAllowedImageFile(file.name)) {
            setUploadError(true);
            return;
        }

        try {
            const dataUrl = await readImageAsDataUri(file);
            setUploadError(false);
            onChange({ name: file.name, data: dataUrl });
        } catch {
            setUploadError(true);
        }
    };

    const handleDrop = (event) => {
        event.preventDefault();
        setIsDragging(false);
        const file = event.dataTransfer?.files?.[0];
        if (file) {
            handleFile(file);
        }
    };

    const handleClear = () => {
        setUploadError(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
        onChange(null);
    };

    return (
        <div className="site-customization-image-field">
            <span className="site-customization-field__label">{label}</span>
            <div
                className={`site-customization-image-field__dropzone ${
                    isDragging ? "site-customization-image-field__dropzone--dragging" : ""
                }`}
                onDragOver={(event) => {
                    event.preventDefault();
                    setIsDragging(true);
                }}
                onDragLeave={(event) => {
                    event.preventDefault();
                    setIsDragging(false);
                }}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        fileInputRef.current?.click();
                    }
                }}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".png,.jpg,.jpeg,.gif,.svg"
                    className="site-customization-image-field__input"
                    onChange={(event) => {
                        const file = event.target.files?.[0];
                        if (file) {
                            handleFile(file);
                        }
                    }}
                />
                <CameraIcon />
                <p className="site-customization-image-field__drop-text">
                    Drag and drop image to upload or{" "}
                    <span className="site-customization-image-field__browse-text">
                        click to browse
                    </span>
                </p>
            </div>

            <p className="site-customization-field__hint">
                Allowed file types: PNG, JPG, JPEG, GIF, SVG
            </p>
            {hint ? <p className="site-customization-field__hint">{hint}</p> : null}

            {displayedFileName ? (
                <div className="site-customization-image-field__status-row">
                    <span className="site-customization-image-field__filename">
                        {displayedFileName}
                    </span>
                    {hasCustomImage ? (
                        <button
                            type="button"
                            className="site-customization-image-field__remove-btn"
                            aria-label={`Remove ${label}`}
                            onClick={handleClear}
                        >
                            ×
                        </button>
                    ) : null}
                </div>
            ) : null}

            {uploadError ? (
                <p className="site-customization-image-field__error" role="alert">
                    <AlertIcon />
                    File unable to upload please try again
                </p>
            ) : null}
        </div>
    );
};

/**
 * Left-hand site-customization form. Renders the template picker plus every
 * field enabled for the active template. Pure presentational — all values are
 * controlled and every edit is emitted through callbacks.
 *
 * @param {object} props
 * @param {Object} props.data - Current customization config.
 * @param {Record<string, boolean>} props.enabledFields - Field visibility gates.
 * @param {Object} props.errors - Validation error map.
 * @param {Array<string>} props.templateTitleOptions - Selectable template titles.
 * @param {string} props.selectedTemplateTitle - Title of the active template.
 * @param {(templateId: string) => void} props.onTemplateChange
 * @param {(field: string, value: *) => void} props.onFieldChange
 * @param {(field: string, index: number, value: string) => void} props.onListItemChange
 * @param {(subField: string, value: string) => void} props.onCentivoCareChange
 * @param {(field: string, fileName: string | null) => void} props.onImageChange
 * @returns {import('react').JSX.Element}
 */
const CustomizationForm = ({
    data,
    enabledFields,
    errors,
    templateTitleOptions,
    selectedTemplateTitle,
    onTemplateChange,
    onFieldChange,
    onListItemChange,
    onCentivoCareChange,
    onImageChange,
}) => (
    <div className="site-customization-form">
        <div className="site-customization-field">
            <label htmlFor="template-select">Select template</label>
            <select
                id="template-select"
                value={selectedTemplateTitle}
                onChange={(event) =>
                    onTemplateChange(
                        getTemplateIdByTitle(
                            event.target.value,
                        ),
                    )
                }
            >
                {templateTitleOptions.map((title) => (
                    <option key={title} value={title}>
                        {title}
                    </option>
                ))}
            </select>
        </div>

        {enabledFields.logo ? (
            <ImageField
                label="Logo image"
                value={data.logo}
                defaultFileName={DEFAULT_IMAGE_FILE_NAMES.logo}
                onChange={(imageObj) => onImageChange("logo", imageObj)}
            />
        ) : null}

        {enabledFields.alreadyMember ? (
            <ToggleRow
                label="Already a member link"
                checked={Boolean(data.alreadyMember)}
                onToggle={(next) => onFieldChange("alreadyMember", next)}
            />
        ) : null}

        {enabledFields.externalLink ? (
            <ToggleRow
                label="External Link"
                checked={Boolean(data.externalLinkEnabled)}
                onToggle={(next) => onFieldChange("externalLinkEnabled", next)}
                errorMessage={
                    errors.externalLink
                        ? "Invalid fields"
                        : errors.externalLinkLabel
                          ? "Invalid label"
                          : errors.externalLinkUrl
                            ? "Invalid URL"
                            : undefined
                }
            >
                <input
                    type="text"
                    placeholder="Link Label"
                    value={data.externalLinkLabel ?? ""}
                    onChange={(event) =>
                        onFieldChange("externalLinkLabel", event.target.value)
                    }
                />
                <input
                    type="text"
                    placeholder="Link Url"
                    value={data.externalLinkUrl ?? ""}
                    onChange={(event) =>
                        onFieldChange("externalLinkUrl", event.target.value)
                    }
                />
            </ToggleRow>
        ) : null}

        {enabledFields.phone ? (
            <ToggleRow
                label="Phone"
                checked={Boolean(data.phoneEnabled)}
                onToggle={(next) => onFieldChange("phoneEnabled", next)}
                errorMessage={errors.phone ? "Invalid phone number" : undefined}
            >
                <input
                    type="text"
                    maxLength={12}
                    placeholder="_ _ _ - _ _ _ - _ _ _ _"
                    value={data.phone ?? ""}
                    onChange={(event) =>
                        onFieldChange("phone", event.target.value)
                    }
                />
            </ToggleRow>
        ) : null}

        {enabledFields.headline ? (
            <div className="site-customization-field">
                <label htmlFor="headline-input">Headline</label>
                <textarea
                    id="headline-input"
                    maxLength={80}
                    placeholder={PLACEHOLDERS.headline[data.selectedTemplate]}
                    value={data.headline ?? ""}
                    onChange={(event) =>
                        onFieldChange("headline", event.target.value)
                    }
                />
            </div>
        ) : null}

        {enabledFields.description ? (
            <div className="site-customization-field">
                <label htmlFor="description-input">Description</label>
                <textarea
                    id="description-input"
                    maxLength={600}
                    placeholder={PLACEHOLDERS.description}
                    value={data.description ?? ""}
                    onChange={(event) =>
                        onFieldChange("description", event.target.value)
                    }
                />
            </div>
        ) : null}

        {enabledFields.hero ? (
            <ImageField
                label="Hero image"
                hint="Large size 616x420, Small size 440x300"
                value={data.hero}
                defaultFileName={DEFAULT_IMAGE_FILE_NAMES.hero}
                onChange={(imageObj) => onImageChange("hero", imageObj)}
            />
        ) : null}

        {enabledFields.video ? (
            <ToggleRow
                label="Video"
                checked={Boolean(data.videoEnabled)}
                onToggle={(next) => onFieldChange("videoEnabled", next)}
                errorMessage={errors.video ? "Invalid URL" : undefined}
            >
                <input
                    type="text"
                    placeholder="Enter URL link"
                    value={data.video ?? ""}
                    onChange={(event) =>
                        onFieldChange("video", event.target.value)
                    }
                />
            </ToggleRow>
        ) : null}

        {enabledFields.heroButton ? (
            <ToggleRow
                label="Show hero button"
                checked={Boolean(data.heroButtonEnabled)}
                onToggle={(next) => onFieldChange("heroButtonEnabled", next)}
                errorMessage={
                    errors.heroButton
                        ? "Invalid fields"
                        : errors.heroButtonLabel
                          ? "Invalid label"
                          : errors.heroButtonUrl
                            ? "Invalid URL"
                            : undefined
                }
            >
                <input
                    type="text"
                    placeholder="Button label"
                    value={data.heroButtonText ?? ""}
                    onChange={(event) =>
                        onFieldChange("heroButtonText", event.target.value)
                    }
                />
                <input
                    type="text"
                    placeholder="Button link"
                    value={data.heroButtonUrl ?? ""}
                    onChange={(event) =>
                        onFieldChange("heroButtonUrl", event.target.value)
                    }
                />
            </ToggleRow>
        ) : null}

        {enabledFields.withCentivo ? (
            <Accordion label="With Centivo, you'll get">
                {PLACEHOLDERS.withCentivo.map((placeholder, index) => (
                    <div className="site-customization-field" key={placeholder}>
                        <label>{`Box ${index + 1}`}</label>
                        <textarea
                            maxLength={155}
                            placeholder={placeholder}
                            value={data.withCentivo?.[index] ?? ""}
                            onChange={(event) =>
                                onListItemChange(
                                    "withCentivo",
                                    index,
                                    event.target.value,
                                )
                            }
                        />
                    </div>
                ))}
            </Accordion>
        ) : null}

        {enabledFields.centivoCare ? (
            <ToggleRow
                label="Centivo Care"
                checked={Boolean(data.vpc)}
                onToggle={(next) => onFieldChange("vpc", next)}
            >
                <input
                    type="text"
                    placeholder="Enter title"
                    value={data.centivoCare?.title ?? ""}
                    onChange={(event) =>
                        onCentivoCareChange("title", event.target.value)
                    }
                />
                <label>Body</label>
                <textarea
                    value={data.centivoCare?.body ?? ""}
                    onChange={(event) =>
                        onCentivoCareChange("body", event.target.value)
                    }
                />
                <input
                    type="text"
                    placeholder="Enter link text"
                    value={data.centivoCare?.text ?? ""}
                    onChange={(event) =>
                        onCentivoCareChange("text", event.target.value)
                    }
                />
                <input
                    type="text"
                    placeholder="Enter URL link"
                    value={data.centivoCare?.link ?? ""}
                    onChange={(event) =>
                        onCentivoCareChange("link", event.target.value)
                    }
                />
            </ToggleRow>
        ) : null}

        {enabledFields.networkOfExcellence ? (
            <Accordion label="Network of excellence">
                {(data.networkOfExcellence ?? []).map((value, index) => (
                    <div className="site-customization-field" key={index}>
                        <textarea
                            maxLength={500}
                            placeholder={PLACEHOLDERS.networkOfExcellence}
                            value={value ?? ""}
                            onChange={(event) =>
                                onListItemChange(
                                    "networkOfExcellence",
                                    index,
                                    event.target.value,
                                )
                            }
                        />
                    </div>
                ))}
            </Accordion>
        ) : null}

        {enabledFields.disclaimer ? (
            <ToggleRow
                label="Disclaimer"
                checked={Boolean(data.disclaimer)}
                onToggle={(next) => onFieldChange("disclaimer", next)}
            />
        ) : null}

        {enabledFields.testimonials ? (
            <ToggleRow
                label="Testimonials"
                checked={Boolean(data.testimonials)}
                onToggle={(next) => onFieldChange("testimonials", next)}
            />
        ) : null}
    </div>
);

export default CustomizationForm;
